import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../hooks/useNotifications';
import { formatDistanceToNow } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import { Bell, CheckCheck, Trash2, Check, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Notification } from '../services/notificationService';

interface NotificationItemProps {
  notification: Notification;
  onMarkAsRead: (id: string) => void;
  onDelete: (id: string) => void;
  isSelected: boolean;
  onSelect: (id: string, selected: boolean) => void;
  isSelectionMode: boolean;
  onNavigate: (notification: Notification) => void;
}

const getEnhancedMessage = (notification: Notification, t: any) => {
  const { sender_name, pet_name, post_content } = notification.data || {};
  const notificationData = notification.data as any;
  
  // Handle follow notifications (no pet_name needed)
  if (notification.data?.type === 'follow' && sender_name) {
    return t('notifications.messages.startedFollowing', { sender: sender_name });
  }
  
  if (sender_name && pet_name) {
    const truncatedContent = post_content ? 
      (post_content.length > 80 ? `${post_content.substring(0, 80)}...` : post_content) : '';
    
    switch (notification.data?.type) {
      case 'like':
        const likeMessage = post_content ? 
          t('notifications.messages.likedPostWithContent', { sender: sender_name, pet: pet_name, content: truncatedContent }) :
          t('notifications.messages.likedPost', { sender: sender_name, pet: pet_name });
        return (
          <>
            {likeMessage}
            <span className="block text-xs text-blue-600 mt-1">
              {t('notifications.messages.clickToView')}
            </span>
          </>
        );
      case 'comment':
        const commentMessage = post_content ? 
          t('notifications.messages.commentedPostWithContent', { sender: sender_name, pet: pet_name, content: truncatedContent }) :
          t('notifications.messages.commentedPost', { sender: sender_name, pet: pet_name });
        return (
          <>
            {commentMessage}
            <span className="block text-xs text-blue-600 mt-1">
              {t('notifications.messages.clickToView')}
            </span>
          </>
        );
      case 'match_request':
        const matchType = notificationData?.match_type;
        const matchTypeLabel = matchType ? matchType.charAt(0).toUpperCase() + matchType.slice(1) : 'Match';
        return t('notifications.messages.matchRequest', { sender: sender_name, matchType: matchTypeLabel, pet: pet_name });
      case 'match_accepted':
        return t('notifications.messages.matchAccepted');
      default:
        return notification.body;
    }
  }
  
  return notification.body;
};

const NotificationItem: React.FC<NotificationItemProps> = ({ 
  notification, 
  onMarkAsRead, 
  onDelete, 
  isSelected, 
  onSelect, 
  isSelectionMode,
  onNavigate 
}) => {
  const { t } = useTranslation();
  const isClickable = 
    notification.data?.type === 'match_request' || 
    notification.data?.type === 'match_accepted' ||
    notification.data?.type === 'like' ||
    notification.data?.type === 'comment';
  
  return (
    <Card 
      className={`mb-4 ${!notification.read ? 'border-primary' : ''} ${isSelected ? 'ring-2 ring-primary' : ''} ${isClickable ? 'cursor-pointer hover:shadow-md hover:border-blue-400 transition-all' : ''}`}
      onClick={() => isClickable && !isSelectionMode ? onNavigate(notification) : undefined}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          {isSelectionMode && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={(checked) => onSelect(notification.id, !!checked)}
              className="mt-1"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <div className="flex-1">
            <h3 className="font-semibold text-sm mb-1">{notification.title}</h3>
            <p className="text-muted-foreground text-sm mb-2">
              {getEnhancedMessage(notification, t)}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
            </p>
          </div>
          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
            {!isSelectionMode && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onDelete(notification.id)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { 
    notifications, 
    unreadCount, 
    isLoading, 
    error, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification,
    deleteSelected,
    deleteAll,
    isPending 
  } = useNotifications();
  
  const { toast } = useToast();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [singleDeleteId, setSingleDeleteId] = useState<string | null>(null);

  const handleNavigateToMatch = (notification: Notification) => {
    const notificationData = notification.data as any;
    
    // Navigate to post for like/comment notifications
    if (notificationData?.type === 'like' || notificationData?.type === 'comment') {
      if (notificationData?.related_entity_id) {
        navigate(`/post/${notificationData.related_entity_id}`);
        if (!notification.read) {
          markAsRead(notification.id);
        }
      }
    }
    // Navigate to match inbox for match notifications
    else if (notificationData?.type === 'match_request') {
      navigate('/matches');
      if (!notification.read) {
        markAsRead(notification.id);
      }
    } else if (notificationData?.type === 'match_accepted') {
      // Navigate to matches with state to auto-open the specific chat
      const matchId = notificationData.related_entity_id;
      navigate('/matches', { state: { openMatchId: matchId } });
      if (!notification.read) {
        markAsRead(notification.id);
      }
    }
  };

  const handleSelect = (id: string, selected: boolean) => {
    const newSelected = new Set(selectedIds);
    if (selected) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && notifications) {
      setSelectedIds(new Set(notifications.map(n => n.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleDeleteSingle = (id: string) => {
    setSingleDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteSingle = async () => {
    if (singleDeleteId) {
      try {
        await deleteNotification(singleDeleteId);
        toast({
          title: t('notifications.toast.success'),
          description: t('notifications.toast.notificationDeleted'),
        });
      } catch (error) {
        toast({
          variant: "destructive",
          title: t('notifications.toast.error'),
          description: t('notifications.toast.deleteError'),
        });
      }
      setSingleDeleteId(null);
    }
    setDeleteDialogOpen(false);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.size > 0) {
      try {
        await deleteSelected(Array.from(selectedIds));
        toast({
          title: t('notifications.toast.success'),
          description: t('notifications.toast.notificationsDeleted', { count: selectedIds.size }),
        });
        setSelectedIds(new Set());
        setIsSelectionMode(false);
      } catch (error) {
        toast({
          variant: "destructive",
          title: t('notifications.toast.error'),
          description: t('notifications.toast.deleteSelectedError'),
        });
      }
    }
  };

  const handleDeleteAll = () => {
    setDeleteAllDialogOpen(true);
  };

  const confirmDeleteAll = async () => {
    try {
      await deleteAll();
      toast({
        title: t('notifications.toast.success'),
        description: t('notifications.toast.allNotificationsDeleted'),
      });
      setSelectedIds(new Set());
      setIsSelectionMode(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('notifications.toast.error'),
        description: t('notifications.toast.deleteAllError'),
      });
    }
    setDeleteAllDialogOpen(false);
  };

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedIds(new Set());
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedIds.size > 0) {
      try {
        const promises = Array.from(selectedIds).map(id => markAsRead(id));
        await Promise.all(promises);
        toast({
          title: t('notifications.toast.success'),
          description: t('notifications.toast.markedAsRead', { count: selectedIds.size }),
        });
        setSelectedIds(new Set());
        setIsSelectionMode(false);
      } catch (error) {
        toast({
          variant: "destructive",
          title: t('notifications.toast.error'),
          description: t('notifications.toast.markAsReadError'),
        });
      }
    }
  };

  const allSelected = notifications && selectedIds.size === notifications.length;
  const someSelected = selectedIds.size > 0;

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <Alert variant="destructive">
          <AlertDescription>
            {t('notifications.loadError')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] md:h-auto overflow-y-auto pb-20 md:pb-6">
      <div className="container mx-auto p-4 md:p-6 max-w-4xl">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                {t('notifications.title')}
                {unreadCount > 0 && (
                  <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </CardTitle>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSelectionMode}
                  className="min-w-[80px]"
                >
                  {isSelectionMode ? <X className="h-4 w-4" /> : t('notifications.select')}
                </Button>
                {notifications && notifications.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDeleteAll}
                    className="text-destructive hover:text-destructive min-w-[100px]"
                  >
                    <Trash2 className="h-4 w-4 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">{t('notifications.deleteAll')}</span>
                    <span className="sm:hidden">{t('notifications.deleteAllShort')}</span>
                  </Button>
                )}
              </div>
            </div>
          
          {isSelectionMode && notifications && notifications.length > 0 && (
            <div className="flex items-center justify-between pt-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={handleSelectAll}
                />
                <span className="text-sm text-muted-foreground">
                  {t('notifications.selectAll')} ({t('notifications.selectedCount', { count: selectedIds.size })})
                </span>
              </div>
              {someSelected && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleMarkSelectedAsRead}
                    className="min-w-[120px]"
                  >
                    <Check className="h-4 w-4 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">{t('notifications.markAsRead', { count: selectedIds.size })}</span>
                    <span className="sm:hidden">{t('notifications.markAsReadShort', { count: selectedIds.size })}</span>
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleDeleteSelected}
                    className="min-w-[120px]"
                  >
                    <Trash2 className="h-4 w-4 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">{t('notifications.deleteSelected', { count: selectedIds.size })}</span>
                    <span className="sm:hidden">{t('notifications.deleteSelectedShort', { count: selectedIds.size })}</span>
                  </Button>
                </div>
              )}
            </div>
          )}
          
          {unreadCount > 0 && !isSelectionMode && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAllAsRead()}
              disabled={isPending}
              className="w-fit"
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              {t('notifications.markAllAsRead')}
            </Button>
          )}
          </CardHeader>

          <CardContent>
            {notifications && notifications.length > 0 ? (
              <div className="space-y-4">
                {notifications.map((notification) => (
                  <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkAsRead={markAsRead}
                    onDelete={handleDeleteSingle}
                    isSelected={selectedIds.has(notification.id)}
                    onSelect={handleSelect}
                    isSelectionMode={isSelectionMode}
                    onNavigate={handleNavigateToMatch}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Bell className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">{t('notifications.noNotifications')}</h3>
                <p className="text-muted-foreground">
                  {t('notifications.noNotificationsDesc')}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onConfirm={confirmDeleteSingle}
          title={t('notifications.deleteDialog.title')}
          description={t('notifications.deleteDialog.description')}
          itemName={t('notifications.deleteDialog.itemName')}
          isLoading={isPending}
        />
        
        <DeleteConfirmDialog
          open={deleteAllDialogOpen}
          onOpenChange={setDeleteAllDialogOpen}
          onConfirm={confirmDeleteAll}
          title={t('notifications.deleteAllDialog.title')}
          description={t('notifications.deleteAllDialog.description')}
          itemName={t('notifications.deleteAllDialog.itemName')}
          isLoading={isPending}
        />
    </div>
  );
};

export default Notifications;